import {
  ChevronLeft,
  ChevronRight,
  Coins,
  FolderOpen,
  GitBranch,
  Hash,
  Loader2,
  Users,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CostLimitCard } from '../../components/admin/CostLimitCard';
import {
  adjustMonth,
  calculateTotalTokens,
  formatCost,
  formatCurrency,
  formatDailyChartData,
  formatMonth,
  formatMonthlyTrendData,
  formatNumber,
  getCurrentMonth,
  niceMax,
  tooltipStyle,
} from '../../components/admin/chart-utils';
import { UsagePieChart } from '../../components/admin/ModelUsagePieCharts';
import { SummaryCard } from '../../components/admin/SummaryCard';
import { TokenBreakdown } from '../../components/admin/TokenBreakdown';
import { DailyCharts, MonthlyTrendCharts } from '../../components/admin/UsageCharts';
import { useWebAuth } from '../../contexts/WebAuthContext';
import { useTasks, useUsageData } from '../../hooks/use-admin-api';

/**
 * ダッシュボードページ
 */
export function DashboardPage() {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedModel, setSelectedModel] = useState<string>('');
  const { user } = useWebAuth();

  // auditグループに所属しているか確認
  const hasAuditGroup = user?.groups?.includes('audit') ?? false;

  // 使用量データを取得
  const {
    data: usageData,
    isLoading: usageLoading,
    error: usageError,
  } = useUsageData(selectedMonth, selectedModel || undefined);

  // 最近のタスクを取得（全タスク取得後、表示時に10件に絞る）
  // auditグループに所属していない場合はAPIエラーになるが、セクション自体を非表示にするため問題なし
  const { data: recentTasks } = useTasks();

  // 月の前後ナビゲーション
  const handlePrevMonth = () => setSelectedMonth((m) => adjustMonth(m, -1));
  const handleNextMonth = () => setSelectedMonth((m) => adjustMonth(m, 1));
  const isCurrentMonth = selectedMonth === getCurrentMonth();

  // 集計データの計算
  const monthlyTotal = usageData?.monthlyData?.[0];
  const inputTokens = monthlyTotal?.inputTokens || 0;
  const cacheReadTokens = monthlyTotal?.cacheReadTokens || 0;
  const cacheWriteTokens = monthlyTotal?.cacheWriteTokens || 0;
  const outputTokens = monthlyTotal?.outputTokens || 0;
  const totalTokens = monthlyTotal ? calculateTotalTokens(monthlyTotal) : 0;
  const totalCost = monthlyTotal?.cost || 0;
  const totalRequests = monthlyTotal?.requestCount || 0;

  // ユーザー統計
  const userStats = usageData?.userStats;
  const activeUsers = userStats?.activeUsers || 0;
  const totalUsers = userStats?.totalUsers || 0;
  const activeRate = userStats?.activeRate || 0;

  // 日別データをグラフ用に整形（全日分を0埋め）
  const dailyChartData = formatDailyChartData(usageData?.dailyData || [], usageData?.period);

  // 月別推移データをグラフ用に整形（過去13ヶ月分を0埋め）
  const monthlyTrendData = formatMonthlyTrendData(usageData?.monthlyTrend || [], usageData?.period);

  // ランキングデータ
  const rankingData = usageData?.ranking || [];

  // 利用可能なモデル一覧
  const availableModels = usageData?.availableModels || [];

  // モデル別使用量（円グラフ用）
  const modelUsage = usageData?.modelUsage || [];

  if (usageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (usageError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400">データの取得に失敗しました</p>
          <p className="text-muted-foreground text-sm mt-2">
            {usageError instanceof Error ? usageError.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">ダッシュボード</h1>
          <p className="text-muted-foreground mt-1">システム全体の使用状況</p>
        </div>

        {/* フィルター */}
        <div className="flex items-center gap-4">
          {/* モデル選択 */}
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-3 py-2 bg-muted border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">全モデル</option>
            {availableModels.map((model: { modelId: string; displayName: string }) => (
              <option key={model.modelId} value={model.modelId}>
                {model.displayName}
              </option>
            ))}
          </select>

          {/* 月選択 */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-foreground font-medium min-w-32 text-center">
              {formatMonth(selectedMonth)}
            </span>
            <button
              type="button"
              onClick={handleNextMonth}
              disabled={isCurrentMonth}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* サマリーカード */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <SummaryCard
          icon={<Zap className="w-6 h-6" />}
          label="総トークン数"
          value={formatNumber(totalTokens)}
          color="blue"
        />
        <SummaryCard
          icon={<Coins className="w-6 h-6" />}
          label="利用料金"
          value={formatCurrency(totalCost)}
          color="yellow"
        />
        <SummaryCard
          icon={<Hash className="w-6 h-6" />}
          label="リクエスト数"
          value={formatNumber(totalRequests)}
          color="green"
        />
        <SummaryCard
          icon={<Users className="w-6 h-6" />}
          label="アクティブユーザー"
          value={`${activeUsers} / ${totalUsers} (${activeRate}%)`}
          color="purple"
        />
      </div>

      {/* コスト上限設定 */}
      <div className="mb-8">
        <CostLimitCard />
      </div>

      {/* トークン内訳 */}
      <TokenBreakdown
        inputTokens={inputTokens}
        cacheReadTokens={cacheReadTokens}
        cacheWriteTokens={cacheWriteTokens}
        outputTokens={outputTokens}
      />

      {/* 日別グラフ */}
      <DailyCharts data={dailyChartData} />

      {/* 月別推移グラフ */}
      <MonthlyTrendCharts data={monthlyTrendData} />

      {/* ランキング・モデル利用割合 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* 利用回数ランキング（横棒グラフ） */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h2 className="text-lg font-semibold text-foreground mb-4">利用回数ランキング</h2>
          <div className="h-64">
            {rankingData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rankingData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" fontSize={10} domain={[0, niceMax]} />
                  <YAxis
                    type="category"
                    dataKey="userName"
                    stroke="#9ca3af"
                    fontSize={10}
                    width={60}
                  />
                  <Tooltip
                    {...tooltipStyle}
                    formatter={(value: number) => [formatNumber(value), 'リクエスト数']}
                  />
                  <Bar dataKey="requestCount" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                データがありません
              </div>
            )}
          </div>
        </div>

        <UsagePieChart
          title="モデル利用割合（金額）"
          data={modelUsage}
          dataKey="cost"
          tooltipLabel="金額"
          tooltipFormatter={formatCurrency}
          cellKeyPrefix="cell-cost"
        />
        <UsagePieChart
          title="モデル利用割合（回数）"
          data={modelUsage}
          dataKey="requestCount"
          tooltipLabel="回数"
          tooltipFormatter={formatNumber}
          cellKeyPrefix="cell-count"
        />
      </div>

      {/* 最近のタスク（auditグループ所属時のみ表示） */}
      {hasAuditGroup && (
        <div className="bg-card rounded-xl p-6 border border-border">
          <h2 className="text-lg font-semibold text-foreground mb-4">最近のタスク</h2>
          {recentTasks?.tasks && recentTasks.tasks.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-muted-foreground text-sm">
                    <th className="pb-3 font-medium">タイトル</th>
                    <th className="pb-3 font-medium">ユーザー</th>
                    <th className="pb-3 font-medium">ワークスペース</th>
                    <th className="pb-3 font-medium">利用料金</th>
                    <th className="pb-3 font-medium">作成日時</th>
                    <th className="pb-3 font-medium">ステータス</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentTasks.tasks.slice(0, 10).map((task) => (
                    <tr key={task.id} className="hover:bg-muted/50">
                      <td className="py-3">
                        <Link
                          to={`/admin/users/${encodeURIComponent(task.userEmail)}/tasks/${task.id}`}
                          className="text-foreground hover:text-primary-400 transition-colors inline-flex items-center gap-2"
                        >
                          {task.title || '無題のタスク'}
                          {task.versionCount && task.versionCount > 1 && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-accent text-foreground rounded"
                              title={`${task.versionCount}つのバージョン`}
                            >
                              <GitBranch className="w-3 h-3" />
                              {task.versionCount}
                            </span>
                          )}
                        </Link>
                      </td>
                      <td className="py-3">
                        <Link
                          to={`/admin/users/${encodeURIComponent(task.userEmail)}`}
                          className="text-muted-foreground hover:text-primary-400 transition-colors"
                        >
                          {task.userEmail}
                        </Link>
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {task.workspaceName ? (
                          <span className="flex items-center gap-1">
                            <FolderOpen className="w-4 h-4" />
                            {task.workspaceName}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="py-3 text-yellow-400">
                        {task.totalCost !== undefined
                          ? formatCost(task.totalCost)
                          : task.cost !== undefined
                            ? formatCost(task.cost)
                            : '-'}
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {new Date(task.createdAt).toLocaleString('ja-JP')}
                      </td>
                      <td className="py-3">
                        {task.deleted ? (
                          <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded">
                            削除済み
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded">
                            アクティブ
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">タスクがありません</p>
          )}
        </div>
      )}
    </div>
  );
}
